import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useState, useMemo, useEffect, useRef, ChangeEvent } from "react";
import { PreTransaction } from "@/app/services/authorizationService/getAuthorization";
import { AuthorizationColumns } from "./components/AuthorizationColumns";
import { useAuthorizations } from "@/app/hooks/useAuthorizations";
import { PartnerType } from "@/app/utils/partners.enum";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";

export const useAuthorizationTableController = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(500, search);
  const navigate = useNavigate();

  const query = useAuthorizations(debouncedSearch);
  const { catalog } = useProductsCatalog();
  const { columns } = AuthorizationColumns(catalog);

  const [selectedItem, setSelectedItem] = useState<PreTransaction | null>(null);
  const [simulatePanOpen, setSimulatePanOpen] = useState(false);

  const [sheetOpen, setSheetOpen] = useState(false);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const observerRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    return query.data?.pages.flatMap((p) => p.preTransactions) ?? [];
  }, [query.data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (item: PreTransaction) => {
    setSelectedItem(item);
    setSheetOpen(true);
  };

  const simulationMap: Record<string, () => void> = {
    [PartnerType.C6]: () => navigate(`/transacoes/${selectedItem?.customerId?.cpf}/c6`),
    [PartnerType.PAN]: () => setSimulatePanOpen(true),
  }

  const handleSimulate = () => {
    if  (!selectedItem?.customerId?.cpf) return;
    
    simulationMap[selectedItem?.type ?? PartnerType.C6]();
  }

  useEffect(() => {
    if (!observerRef.current || !query.hasNextPage || query.isFetchingNextPage)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          query.fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage]);

  return {
    table,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    selectedItem,
    sheetOpen,
    setSheetOpen,
    handleRowClick,
    observerRef,
    isFetchingNextPage: query.isFetchingNextPage,
    shouldShowInfiniteScroll: query.hasNextPage,
    handleSimulate,
    simulatePanOpen,
    setSimulatePanOpen,
    search,
    handleSearchInputChange,
  };
};
